import { useCallback, useRef } from 'react';
import { ReactEditor } from 'slate-react';
import { useRecoilState } from 'recoil';
import { lineIndicatorState } from 'domains/reporter/Reporter/state';
import { Editor, Element, Range } from '../core';
import { MACRO_PLACEHOLDER_PLUGIN_ID } from '../plugins/macroPlaceholder/types';
import { useCoalescedAnimationFrame } from './useCoalescedAnimationFrame';

/**
 * Normalizes the selection used for line-indicator positioning.
 *
 * When macro placeholders are rendered as void chips, Slate can still hold a collapsed
 * selection inside the chip's hidden text child. Measuring that DOM range causes the
 * line indicator to shift vertically. We remap to an adjacent visible caret point so
 * the indicator remains aligned to the current line.
 */
export const getLineIndicatorSelection = ({
  editor,
  selection,
}: {
  editor: Editor;
  selection: Range;
}) => {
  if (!Range.isCollapsed(selection)) {
    return selection;
  }

  const maybeMacroPlaceholderEntry = Editor.above(editor, {
    at: selection.anchor,
    match: (n): n is Element => Element.isElement(n) && n.type === MACRO_PLACEHOLDER_PLUGIN_ID,
    mode: 'lowest',
    voids: true,
  });

  if (maybeMacroPlaceholderEntry == null) {
    return selection;
  }

  const [macroNode, macroPath] = maybeMacroPlaceholderEntry;
  if (!Editor.isVoid(editor, macroNode)) {
    return selection;
  }

  const pointBefore = Editor.before(editor, macroPath, { voids: true });
  if (pointBefore != null) {
    return { anchor: pointBefore, focus: pointBefore };
  }

  const pointAfter = Editor.after(editor, macroPath, { voids: true });
  if (pointAfter != null) {
    return { anchor: pointAfter, focus: pointAfter };
  }

  return selection;
};

/**
 * Shows the line indicator when a selection is collapsed.
 */
export const useLineIndicator = () => {
  const [lineIndicatorRef, setLineIndicatorRef] = useRecoilState(lineIndicatorState);
  const latestRequestRef = useRef<{ editor: Editor; selection: Range } | null>(null);
  const { schedule: scheduleFallbackUpdate, cancel: cancelFallbackUpdate } =
    useCoalescedAnimationFrame(() => {
      const domSelection = window.getSelection();
      if (domSelection != null && domSelection.rangeCount > 0) {
        const domRange = domSelection.getRangeAt(0);
        setLineIndicatorRef(domRange);
      }
    });

  const flushPositionUpdate = useCallback(() => {
    const latestRequest = latestRequestRef.current;
    if (latestRequest == null) {
      return;
    }

    const { editor, selection } = latestRequest;

    try {
      if (Range.isExpanded(selection)) {
        setLineIndicatorRef(null);
        return;
      }
      const normalizedSelection = getLineIndicatorSelection({ editor, selection });
      const domRange = ReactEditor.toDOMRange(editor, normalizedSelection);
      setLineIndicatorRef(domRange);
    } catch (e) {
      scheduleFallbackUpdate();
    }
  }, [scheduleFallbackUpdate, setLineIndicatorRef]);
  const { schedule: schedulePositionUpdate } = useCoalescedAnimationFrame(flushPositionUpdate);

  const updatePosition = useCallback(
    ({ editor, selection }: { editor: Editor; selection: Range }) => {
      // Always keep only the latest request; stale ones are superseded.
      latestRequestRef.current = { editor, selection };

      cancelFallbackUpdate();
      schedulePositionUpdate();
    },
    [cancelFallbackUpdate, schedulePositionUpdate]
  );

  return {
    lineIndicatorRef,
    setLineIndicatorRef,
    updatePosition,
  };
};
